import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Camera, Trash2, Upload, X, Maximize2, Smartphone } from 'lucide-react';
import { toast } from 'sonner';

/**
 * EvidencePhotoGallery — reusable photo evidence attach + view.
 *
 * Props:
 *   api            — () => axios instance (from AuthContext)
 *   docType        — 'production_order' | 'repair_order' | 'bill_of_lading'
 *   docId          — the order id
 *   evidenceType   — 'work_evidence' | 'scale_ticket' | 'intake'
 *   title          — card title to display (defaults to "Photo Evidence")
 *   emptyHint      — hint shown when no photos uploaded yet
 *   singleShot     — when true, only one photo is kept (replaces on upload). Good for BoL scale ticket.
 *   maxFiles       — soft cap; hides upload button when reached
 *   testIdPrefix   — used to key the data-testids
 *   compact        — compact layout (smaller thumbnails)
 */
const EvidencePhotoGallery = ({
  api, docType, docId, evidenceType = 'work_evidence',
  title = 'Photo Evidence', emptyHint = 'Snap photos as you work and they will appear here.',
  singleShot = false, maxFiles = 24, testIdPrefix = 'evidence', compact = false,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [blobUrls, setBlobUrls] = useState({});
  const [viewer, setViewer] = useState(null);
  const [caption, setCaption] = useState('');
  const [phoneQr, setPhoneQr] = useState(null);   // iter107 — { url, expires }
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!docId || docId === 'new') return;
    setLoading(true);
    try {
      const r = await api().get(`/doc-evidence?doc_type=${docType}&doc_id=${docId}&evidence_type=${evidenceType}`);
      setItems(r.data || []);
    } catch (e) {
      // non-blocking
    } finally { setLoading(false); }
  }, [api, docType, docId, evidenceType]);
  useEffect(() => { load(); }, [load]);

  // Fetch each image as a blob url (image tags cannot send auth headers).
  useEffect(() => {
    let cancelled = false;
    const existing = { ...blobUrls };
    const needed = items.filter(it => !existing[it.id]);
    if (!needed.length) return;
    (async () => {
      const next = { ...existing };
      for (const it of needed) {
        try {
          const res = await api().get(`/doc-evidence/${it.id}/file`, { responseType: 'blob' });
          const url = URL.createObjectURL(res.data);
          next[it.id] = url;
        } catch { /* ignore */ }
      }
      if (!cancelled) setBlobUrls(next);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Cleanup object URLs on unmount
  useEffect(() => () => { Object.values(blobUrls).forEach(u => URL.revokeObjectURL(u)); }, []); // eslint-disable-line

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    if (!docId || docId === 'new') { toast.error('Save the document first before attaching photos.'); return; }
    const list = Array.from(files);
    setUploading(true);
    try {
      // In singleShot mode we delete all existing before uploading the new one so the UI stays clean.
      if (singleShot && items.length) {
        for (const prev of items) {
          try { await api().delete(`/doc-evidence/${prev.id}`); } catch { /* ignore */ }
        }
      }
      for (const f of list) {
        const fd = new FormData();
        fd.append('file', f);
        fd.append('doc_type', docType);
        fd.append('doc_id', docId);
        fd.append('evidence_type', evidenceType);
        if (caption) fd.append('caption', caption);
        await api().post('/doc-evidence/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setCaption('');
      await load();
      toast.success(`${list.length} photo${list.length > 1 ? 's' : ''} uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async (ev) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await api().delete(`/doc-evidence/${ev.id}`);
      await load();
      toast.success('Photo removed');
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  // iter107 — Mint a short-lived phone upload link; show it as a scannable QR
  const openPhoneQr = async () => {
    if (!docId || docId === 'new') { toast.error('Save the document first before using phone upload.'); return; }
    try {
      const r = await api().post('/doc-evidence/phone-token', { doc_type: docType, doc_id: docId, evidence_type: evidenceType });
      const base = api().defaults.baseURL.replace(/\/api$/, '');
      setPhoneQr({ url: `${base}${r.data.upload_page_path}`, expires: r.data.expires });
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create phone link'); }
  };

  const disabled = uploading || (singleShot ? false : items.length >= maxFiles);
  const thumbSize = compact ? 'h-24 w-24' : 'h-36 w-36';

  return (
    <Card className="industrial-card" data-testid={`${testIdPrefix}-gallery`}>
      <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base flex items-center gap-2">
          <Camera className="w-4 h-4 text-slate-600" />
          {title}
          <span className="text-xs text-slate-400 font-normal">{items.length}{singleShot ? '' : `/${maxFiles}`}</span>
        </CardTitle>
        {(docId && docId !== 'new') && (
          <div className="flex items-center gap-2 flex-wrap">
            {!singleShot && (
              <Input
                placeholder="Optional caption for next upload…"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                className="rounded-sm h-9 w-56 text-xs"
                data-testid={`${testIdPrefix}-caption-input`}
              />
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple={!singleShot}
              onChange={(e) => handleFiles(e.target.files)}
              className="hidden"
              data-testid={`${testIdPrefix}-file-input`}
            />
            <Button
              size="sm"
              variant="outline"
              className="rounded-sm"
              disabled={disabled}
              onClick={() => fileRef.current && fileRef.current.click()}
              data-testid={`${testIdPrefix}-upload-btn`}
            >
              <Upload className="w-3.5 h-3.5 mr-1" /> {singleShot && items.length ? 'Replace Photo' : 'Add Photos'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="rounded-sm border-sky-300 text-sky-700 hover:bg-sky-50"
              onClick={openPhoneQr}
              data-testid={`${testIdPrefix}-phone-btn`}
              title="Generate a QR a phone can scan to upload photos without logging in"
            ><Smartphone className="w-3.5 h-3.5 mr-1" /> Phone</Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="p-4">
        {(!docId || docId === 'new') ? (
          <div className="text-sm text-slate-500 italic">Save the document first to attach photos.</div>
        ) : loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-500 italic">{emptyHint}</div>
        ) : (
          <div className="flex flex-wrap gap-3">
            {items.map((ev) => (
              <div key={ev.id} className="relative group border border-slate-200 rounded-sm overflow-hidden" data-testid={`${testIdPrefix}-tile-${ev.id}`}>
                {blobUrls[ev.id] ? (
                  <img
                    src={blobUrls[ev.id]}
                    alt={ev.caption || ev.original_filename}
                    className={`${thumbSize} object-cover`}
                    onClick={() => setViewer(ev)}
                    style={{ cursor: 'zoom-in' }}
                  />
                ) : (
                  <div className={`${thumbSize} bg-slate-100 animate-pulse`} />
                )}
                <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] px-1.5 py-1 flex items-center justify-between opacity-0 group-hover:opacity-100 transition">
                  <span className="truncate">{ev.caption || ev.uploaded_by_name || ''}</span>
                  <button
                    className="hover:text-red-300 shrink-0 ml-1"
                    onClick={() => remove(ev)}
                    title="Delete"
                    data-testid={`${testIdPrefix}-delete-${ev.id}`}
                  ><Trash2 className="w-3 h-3" /></button>
                </div>
                <button
                  className="absolute top-1 right-1 bg-black/50 hover:bg-black/80 text-white rounded-sm p-1 opacity-0 group-hover:opacity-100 transition"
                  onClick={() => setViewer(ev)}
                  title="View full-size"
                ><Maximize2 className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        )}
        {viewer && (
          <div
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setViewer(null)}
            data-testid={`${testIdPrefix}-viewer`}
          >
            <button
              onClick={() => setViewer(null)}
              className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white rounded-sm p-2"
              data-testid={`${testIdPrefix}-viewer-close`}
            ><X className="w-5 h-5" /></button>
            {blobUrls[viewer.id] && (
              <img src={blobUrls[viewer.id]} alt={viewer.caption || ''} className="max-h-[90vh] max-w-[90vw] object-contain" onClick={(e) => e.stopPropagation()} />
            )}
            {viewer.caption && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-3 py-1.5 rounded-sm text-sm">{viewer.caption}</div>
            )}
          </div>
        )}
        {phoneQr && (
          <div
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
            onClick={() => setPhoneQr(null)}
            data-testid={`${testIdPrefix}-phone-modal`}
          >
            <div className="bg-white rounded-sm p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <div className="font-semibold flex items-center gap-2"><Smartphone className="w-4 h-4 text-sky-600" /> Phone upload link</div>
                <button className="text-slate-400 hover:text-slate-900" onClick={() => setPhoneQr(null)} data-testid={`${testIdPrefix}-phone-close`}><X className="w-4 h-4" /></button>
              </div>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${encodeURIComponent(phoneQr.url)}`}
                alt="QR to open the uploader"
                className="w-full border border-slate-200 rounded-sm"
              />
              <div className="text-[11px] mono text-slate-500 mt-2 break-all">{phoneQr.url}</div>
              <div className="text-xs text-slate-500 mt-2">Scan with any phone camera to open the lightweight uploader. No login required. Link expires {new Date(phoneQr.expires).toLocaleString()}.</div>
              <Button
                variant="outline"
                className="rounded-sm w-full mt-3"
                onClick={() => { navigator.clipboard.writeText(phoneQr.url); toast.success('Link copied'); }}
                data-testid={`${testIdPrefix}-phone-copy`}
              >Copy link</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EvidencePhotoGallery;
